import React, { useState, useEffect, useMemo } from 'react';
import {
	MaterialReactTable,
	useMaterialReactTable,
} from 'material-react-table';
import { makeApiRequest } from '../../Shared/Api';
import {
	Box,
	Button,
	IconButton,
	Tooltip,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import EditOrganization from '../../Components/Modals/EditOrganization';
import MobileCreateFab from '../../Components/MobileCreateFab';
import { useAppContext, ACTION_TYPES } from '../../Context/AppContext';
import OrganizationsMobileList from './components/OrganizationsMobileList';

const Organizations = () => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
	const [organizations, setOrganizations] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedOrganization, setSelectedOrganization] = useState(null);
	const { dispatch } = useAppContext();

	const fetchOrganizations = async () => {
		setIsLoading(true);
		try {
			const response = await makeApiRequest('GET', '/auth/organizations/');
			setOrganizations(response || []);
		} catch (error) {
			console.error('Failed to fetch organizations:', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Organizations' },
		});
		fetchOrganizations();
	}, [dispatch]);

	const handleOpen = (organization = null) => {
		setSelectedOrganization(organization);
		setModalOpen(true);
	};

	const handleClose = () => {
		setModalOpen(false);
		setSelectedOrganization(null);
	};

	const columns = useMemo(
		() => [
			{
				header: 'Name',
				accessorKey: 'name',
			},
			{
				header: 'Slug',
				accessorKey: 'slug',
			},
			{
				header: 'Invoice payee',
				accessorKey: 'invoice_payee',
			},
			{
				header: 'From email',
				accessorKey: 'from_email',
			},
		],
		[],
	);

	const renderCreateOrganizationButton = () => (
		<Button
			variant="contained"
			startIcon={<AddIcon />}
			onClick={() => handleOpen()}
			sx={{
				textTransform: 'none',
				fontWeight: 600,
			}}
		>
			Create Organization
		</Button>
	);

	const table = useMaterialReactTable({
		columns,
		data: organizations,
		layoutMode: 'semantic',
		enableRowActions: true,
		positionActionsColumn: 'last',
		state: {
			isLoading,
		},
		renderRowActions: ({ row }) => (
			<Box sx={{ display: 'flex', gap: '1rem' }}>
				<Tooltip title="Edit">
					<IconButton onClick={() => handleOpen(row.original)}>
						<EditIcon />
					</IconButton>
				</Tooltip>
			</Box>
		),
		renderTopToolbarCustomActions: renderCreateOrganizationButton,
		muiTablePaperProps: {
			elevation: 2,
			sx: {
				borderRadius: '8px',
			},
		},
		muiTableHeadCellProps: {
			sx: {
				fontWeight: 'bold',
				fontSize: '0.95rem',
			},
		},
	});

	return (
		<Box sx={{ width: '100%' }}>
			{isDesktop ? (
				<MaterialReactTable table={table} />
			) : (
				<>
					<OrganizationsMobileList
						organizations={organizations}
						isLoading={isLoading}
						onEdit={handleOpen}
					/>
					<MobileCreateFab
						label="Create Organization"
						onClick={() => handleOpen()}
					/>
				</>
			)}
			<EditOrganization
				open={modalOpen}
				onClose={handleClose}
				onSubmit={fetchOrganizations}
				selectedOrganization={selectedOrganization}
			/>
		</Box>
	);
};

export default Organizations;
